<!--
 * @Description: 支付分账户（收钱吧）
-->
<template>
  <scroll-layout class="payment-score-account-list">
    <template slot="header">
      <!-- 查询条件 -->
      <ykc-search
        ref="YkcSearch"
        :data="searchData"
        :searchForm="searchParams"
        @clean="handleSearchClean"
        @click="handleSearchConfirm"></ykc-search>
    </template>

    <ykc-table
      ref="YkcTable"
      :data="tableData"
      :title="tableTitle"
      :columns="tableColumns"
      :operateButtons="tableOperateButtons.filter(item => item.enabled())">
      <template #headerButtons>
        <div class="table-header-button">
          <ykc-button
            v-rbac="'settlement:manage:paymentScore:export'"
            type="plain"
            @click="doExport">
            导出
          </ykc-button>
        </div>
      </template>

      <ykc-pagination
        align="right"
        slot="pagination"
        ref="YkcPagination"
        :total="pageInfo.total"
        :page-size.sync="pageInfo.size"
        :current-page.sync="pageInfo.current"
        @size-change="handlePaginationSizeChange"
        @current-change="handlePaginationCurrentChange"></ykc-pagination>
    </ykc-table>
  </scroll-layout>
</template>

<script>
  import { offlineExport } from '@/utils';
  import { paymentScoreAccount } from '@/service/apis';

  const FLOW_TYPE_OPTIONS = [
    { id: '1', name: '支付分订单-代扣金额' },
    { id: '2', name: '订单代扣-收钱吧手续费' },
    { id: '3', name: '支付分订单-退款金额' },
    { id: '4', name: '订单代扣-收钱吧手续费退回' },
    { id: '5', name: '支付分订单-清分金额' },
  ];

  const FLOW_DIRECTION_OPTIONS = [
    { id: '1', name: '收入' },
    { id: '2', name: '支出' },
  ];

  const FLOW_TARGET_OPTIONS = [
    { id: '1', name: '用户' },
    { id: '2', name: '微信' },
    { id: '3', name: '备付金银行' },
    { id: '4', name: '平台子账户' },
  ];

  export default {
    name: 'PaymentScoreAccountList',
    data() {
      return {
        tableTitle: '账户流水记录',
        tableData: [],
        tableOperateButtons: [],
        tableColumns: [
          {
            label: '流水号',
            prop: 'flowNumber',
            minWidth: '180px',
            scopedSlots: {
              default: ({ row }) => this.renderFlowNoCell(row.flowNumber),
            },
          },
          { label: '流水时间', prop: 'flowTime', minWidth: '160px' },
          {
            label: '流水金额（元）',
            prop: 'flowAmount',
            minWidth: '130px',
            scopedSlots: {
              default: ({ row }) => this.formatAmount(row.flowAmount),
            },
          },
          {
            label: '流水类型',
            prop: 'flowTypeStr',
            minWidth: '220px',
          },
          {
            label: '收支方向',
            prop: 'flowDirectionStr',
            minWidth: '100px',
          },
          {
            label: '流水对象',
            prop: 'flowTargetTypeStr',
            minWidth: '120px',
          },
          {
            label: '回执单号',
            prop: 'receiptNo',
            minWidth: '180px',
            scopedSlots: {
              default: ({ row }) => row.receiptNo || '—',
            },
          },
        ],
        pageInfo: {
          current: 1,
          size: 10,
          total: 0,
        },
        searchParams: {
          flowNumber: '',
          flowType: '',
          flowDirection: '',
          flowTarget: '',
          flowTimeRange: [],
        },
      };
    },
    computed: {
      searchData() {
        const all = { id: '', name: '全部' };
        return [
          {
            comName: 'YkcInput',
            key: 'flowNumber',
            label: '流水号',
            placeholder: '请输入流水号',
          },
          {
            comName: 'YkcDropdown',
            key: 'flowType',
            label: '流水类型',
            placeholder: '请选择流水类型',
            data: [all, ...FLOW_TYPE_OPTIONS],
          },
          {
            comName: 'YkcDropdown',
            key: 'flowDirection',
            label: '收支方向',
            placeholder: '请选择收支方向',
            data: [all, ...FLOW_DIRECTION_OPTIONS],
          },
          {
            comName: 'YkcDropdown',
            key: 'flowTarget',
            label: '流水对象',
            placeholder: '请选择流水对象',
            data: [all, ...FLOW_TARGET_OPTIONS],
          },
          {
            comName: 'YkcDatePicker',
            key: 'flowTimeRange',
            label: '流水时间',
            type: 'daterange',
            startPlaceholder: '开始日期',
            endPlaceholder: '结束日期',
          },
        ];
      },
    },
    created() {
      this.initData();
      this.requestList();
    },
    methods: {
      initData() {
        this.tableOperateButtons = [];
      },
      formatAmount(value) {
        if (value === undefined || value === null || value === '') {
          return '—';
        }
        const num = Number(value);
        return Number.isNaN(num) ? value : `¥${num.toFixed(2)}`;
      },
      /**
       * 渲染流水号单元格（鼠标悬停显示复制按钮）
       */
      renderFlowNoCell(flowNumber) {
        if (!flowNumber) {
          return '—';
        }
        return (
          <span class="flow-no-cell">
            <span class="flow-no-text" title={flowNumber}>
              {flowNumber}
            </span>
            <i
              class="el-icon-document-copy flow-no-copy-btn"
              title="复制"
              onClick={event => {
                event.stopPropagation();
                this.handleCopyFlowNumber(flowNumber);
              }}></i>
          </span>
        );
      },
      /**
       * 复制流水号
       */
      handleCopyFlowNumber(flowNumber) {
        this.$copyText(flowNumber)
          .then(() => {
            this.$message.success('复制成功');
          })
          .catch(() => {
            this.$message.error('复制失败');
          });
      },
      /**
       * 结束日期补全到当天最后一秒
       */
      formatDateEnd(date) {
        return date ? `${date} 23:59:59` : '';
      },
      /**
       * 请求列表数据
       */
      requestList() {
        const params = {
          flowNumber: this.searchParams.flowNumber,
          flowType: this.searchParams.flowType,
          flowDirection: this.searchParams.flowDirection,
          flowTarget: this.searchParams.flowTarget,
          flowTimeStart: this.searchParams.flowTimeRange?.[0] || '',
          flowTimeEnd: this.formatDateEnd(this.searchParams.flowTimeRange?.[1]),
          current: this.pageInfo.current,
          size: this.pageInfo.size,
        };
        paymentScoreAccount.list(params).then(response => {
          this.tableData = (response && response.records) || [];
          this.pageInfo.total = (response && response.total) || 0;
        });
      },
      /**
       * 点击筛选查询按钮
       */
      handleSearchConfirm(searchData) {
        Object.assign(this.searchParams, searchData);
        this.pageInfo.current = 1;
        this.requestList();
      },
      /**
       * 点击清空查询按钮
       */
      handleSearchClean(form) {
        Object.assign(this.searchParams, form);
        this.searchParams.flowTimeRange = [];
      },
      /**
       * 分页大小变化
       */
      handlePaginationSizeChange(size) {
        this.pageInfo.current = 1;
        this.pageInfo.size = size;
        this.requestList();
      },
      /**
       * 分页页码变化
       */
      handlePaginationCurrentChange(current) {
        this.pageInfo.current = current;
        this.requestList();
      },
      /**
       * 导出
       */
      doExport() {
        offlineExport(
          {
            downloadType: 'finance_export',
            jsonNode: {
              downloadKey: 'splitPaymentAccountFlowExport',
              flowNumber: this.searchParams.flowNumber,
              flowType: this.searchParams.flowType,
              flowDirection: this.searchParams.flowDirection,
              flowTarget: this.searchParams.flowTarget,
              flowTimeStart: this.searchParams.flowTimeRange?.[0] || '',
              flowTimeEnd: this.formatDateEnd(this.searchParams.flowTimeRange?.[1]),
            },
          },
          this.pageInfo.total
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  .payment-score-account-list {
    // 流水号单元格：鼠标悬停显示复制按钮
    .flow-no-cell {
      display: inline-flex;
      align-items: center;
      max-width: 100%;

      .flow-no-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .flow-no-copy-btn {
        flex-shrink: 0;
        margin-left: 4px;
        color: #1890ff;
        cursor: pointer;
        visibility: hidden;

        &:hover {
          color: #40a9ff;
        }
      }

      &:hover .flow-no-copy-btn {
        visibility: visible;
      }
    }
  }
</style>
